import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BOM_STATUSES, BOM_LABEL, bomTone, pillClass } from "@/lib/factory";
import { uploadMany, fileUrl, authedApiUrl } from "@/lib/uploads";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";
import {
  Upload, Trash2, Truck, Download, Users,
  ClipboardList, Package, Camera, FileDown,
} from "lucide-react";

// Only the 5 core statuses appear in the edit dropdown (per Feb 2026 spec).
// Legacy keys (payment_pending, discontinued) still get their friendly label
// via BOM_LABEL if they linger on old rows.
const BOM_EDIT_STATUSES = BOM_STATUSES.filter(
  (s) => !["payment_pending", "discontinued"].includes(s.key)
);

/**
 * Adaptive workspace for Purchase Manager & Purchase Executive.
 * - Manager sees all orders + allocation + BOM import.
 * - Executive sees only assigned orders.
 * - Read-only view (Design/CRM) is handled via disabled controls (server enforces).
 */
export default function ProcurementPage() {
  const { isAdmin, roleName, roleType, principal } = useAuth();
  const canWrite = ["Purchase Manager", "Purchase Executive",
                    "Procurement Manager", "Procurement Executive",
                    "Production Manager", "Production Executive"].includes(roleName)
                   || isAdmin || roleType === "director";

  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/procurement/orders");
      setOrders(data);
      if (!selectedId && data.length > 0) setSelectedId(data[0].id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [selectedId]);

  const loadSelected = useCallback(async (id) => {
    if (!id) return setSelected(null);
    try {
      const { data } = await api.get(`/procurement/orders/${id}`);
      setSelected(data);
    } catch { setSelected(null); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSelected(selectedId); }, [selectedId, loadSelected]);
  useEffect(() => {
    const t = setInterval(() => { load(); if (selectedId) loadSelected(selectedId); }, 30000);
    return () => clearInterval(t);
  }, [load, loadSelected, selectedId]);

  const filtered = useMemo(() =>
    orders.filter(o => projectMatches(o, q)),
    [orders, q]);

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
          Phase 6 · {canWrite ? "Purchase Console" : "Factory Feed"}
        </p>
        <h1 className="font-display text-3xl text-white mt-1">Sourcing & Purchasing</h1>
      </div>

      <div className="px-10 py-6 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 items-start">
        <div className="space-y-2">
          <ProjectSearchBar
            value={q}
            onChange={setQ}
            resultCount={filtered.length}
            totalCount={orders.length}
            testid="proc-search"
          />
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">Orders</p>
          {orders.length === 0 ? (
            <div className="iz-card p-6 text-center">
              <Package className="w-6 h-6 text-gray-700 mx-auto mb-2" />
              <p className="text-xs font-mono text-gray-500">No factory-bound projects yet.</p>
            </div>
          ) : filtered.length === 0 ? (
            <ProjectSearchEmpty query={q} testid="proc-search-empty" />
          ) : filtered.map(o => {
            const total = o.bom_total || 0;
            const delivered = o.bom_counts?.delivered || 0;
            const pct = total ? Math.round((delivered / total) * 100) : 0;
            return (
              <button key={o.id}
                      onClick={() => setSelectedId(o.id)}
                      className={`iz-card p-3 w-full text-left hover:border-[#d4af37]/40 transition-colors ${selectedId === o.id ? "border-[#d4af37]" : ""}`}
                      data-testid={`proc-row-${o.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-white text-sm font-medium truncate">{o.client_name}{o.city ? ` · ${o.city}` : ""}</p>
                  {!o.assigned_purchase_executive_id && <span className="text-[9px] font-mono text-[#d4af37]">UNASSIGNED</span>}
                </div>
                <p className="text-[11px] text-gray-500 truncate">{o.project_name}</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 h-1 bg-[#111] rounded overflow-hidden">
                    <div className="h-full bg-emerald-400" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[10px] font-mono text-gray-500">{delivered}/{total}</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="min-w-0">
          {selected ? (
            <OrderDetail
              order={selected}
              canWrite={canWrite}
              principalId={principal?.id}
              onRefresh={() => { load(); loadSelected(selectedId); }}
            />
          ) : (
            <div className="iz-card p-12 text-center">
              <ClipboardList className="w-8 h-8 text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Select an order.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OrderDetail({ order, canWrite, principalId, onRefresh }) {
  const [tab, setTab] = useState("bom");
  const [showImport, setShowImport] = useState(false);
  const [showManifest, setShowManifest] = useState(false);

  return (
    <div className="space-y-4">
      <div className="iz-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Procurement Order</p>
            <h2 className="font-display text-2xl text-white mt-1">{order.client_name}{order.city ? ` · ${order.city}` : ""}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{order.project_name}</p>
          </div>
        </div>

        {/* Import / Log-manifest actions */}
        {canWrite && (
          <div className="mt-5 pt-4 border-t border-[#1a1a1a] flex justify-end gap-2">
            <button onClick={() => setShowImport(true)} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="import-bom">
              <Upload className="w-3.5 h-3.5"/> Import BOM (CSV)
            </button>
            <button onClick={() => setShowManifest(true)} className="iz-btn-secondary text-xs flex items-center gap-1.5" data-testid="log-manifest">
              <Truck className="w-3.5 h-3.5"/> Log manifest
            </button>
          </div>
        )}
      </div>

      <div className="flex border-b border-[#1a1a1a] gap-6">
        <TabButton active={tab === "bom"} onClick={() => setTab("bom")} testid="tab-bom">
          BOM Line Items ({(order.bom_items || []).length})
        </TabButton>
        <TabButton active={tab === "manifests"} onClick={() => setTab("manifests")} testid="tab-manifests">
          Logistics Manifests ({(order.manifests || []).length})
        </TabButton>
        <TabButton active={tab === "dispatches"} onClick={() => setTab("dispatches")} testid="tab-dispatches">
          Purchase Dispatches
        </TabButton>
      </div>

      {tab === "bom" && (
        <BomTable order={order} canWrite={canWrite} onRefresh={onRefresh} />
      )}
      {tab === "manifests" && (
        <ManifestsList order={order} />
      )}
      {tab === "dispatches" && (
        <PurchaseDispatchesList order={order} canWrite={canWrite} onRefresh={onRefresh} />
      )}

      {showImport && <ImportBomModal order={order} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); onRefresh(); }} />}
      {showManifest && <ManifestModal order={order} onClose={() => setShowManifest(false)} onDone={() => { setShowManifest(false); onRefresh(); }} />}
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} className={`pb-2.5 text-sm transition-colors border-b-2 ${active ? "text-[#d4af37] border-[#d4af37]" : "text-gray-500 border-transparent hover:text-gray-300"}`} data-testid={testid}>
      {children}
    </button>
  );
}

function PurchaseDispatchesList({ order, canWrite, onRefresh }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nowTs, setNowTs] = useState(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/procurement/orders/${order.id}/purchase-dispatches`);
      setRows(data.dispatches || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [order.id]);
  useEffect(() => { load(); }, [load]);

  // Tick every 15s so the "Undo (2m left)" countdown stays honest.
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  const undo = async (d) => {
    if (!window.confirm(`Undo dispatch of ${(d.bom_items || []).length} item(s)? The site team's entry will be removed.`)) return;
    try {
      await api.delete(`/procurement/dispatches/${d.id}`);
      toast.success("Dispatch undone — supervisor notified");
      await load();
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Undo failed");
    }
  };

  if (loading) {
    return <div className="iz-card p-6 text-xs text-gray-500">Loading dispatches…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="iz-card p-8 text-center" data-testid="purchase-dispatches-empty">
        <Truck className="w-6 h-6 text-gray-700 mx-auto mb-2" />
        <p className="text-xs text-gray-500">No Purchase-console dispatches logged yet on this order.</p>
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="purchase-dispatches-list">
      {rows.map((d) => {
        const deadlineMs = d.undo_deadline_at ? new Date(d.undo_deadline_at).getTime() : 0;
        const remaining = deadlineMs - nowTs;
        const canUndo = canWrite && remaining > 0;
        const minsLeft = Math.max(0, Math.ceil(remaining / 60000));
        return (
          <li key={d.id} className="iz-card p-4"
              data-testid={`purchase-dispatch-${d.id}`}>
            <div className="flex justify-between gap-3 flex-wrap items-start">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white">{d.description}</p>
                {(d.driver_name || d.vehicle_number) && (
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Driver {d.driver_name || "—"} · Vehicle <span className="font-mono">{d.vehicle_number || "—"}</span>
                  </p>
                )}
                {Array.isArray(d.bom_items) && d.bom_items.length > 0 && (
                  <ul className="mt-2 border-l border-[#262626] pl-3 space-y-0.5">
                    {d.bom_items.map((b, i) => (
                      <li key={b.bom_item_id || i} className="text-[10px] text-gray-400">
                        <span className="text-white">{b.item_name}</span>
                        {b.vendor && <span className="text-gray-500"> · {b.vendor}</span>}
                        <span className="font-mono text-gray-500 ml-1">
                          · {b.qty}{b.unit ? ` ${b.unit}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[10px] text-gray-600 mt-2">
                  {new Date(d.dispatched_at).toLocaleString()} · by {d.dispatched_by_name}
                </p>
              </div>
              {canUndo ? (
                <button onClick={() => undo(d)}
                        className="iz-btn-secondary text-xs flex items-center gap-1 shrink-0"
                        data-testid={`undo-dispatch-${d.id}`}>
                  Undo · {minsLeft}m left
                </button>
              ) : (
                <span className="text-[10px] font-mono uppercase tracking-widest text-gray-600 shrink-0">
                  Undo window expired
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function BomTable({ order, canWrite, onRefresh }) {
  const items = order.bom_items || [];
  const [vendors, setVendors] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [showBulk, setShowBulk] = useState(false);
  const [showDispatch, setShowDispatch] = useState(false);

  // Load saved vendors once for the datalist autocomplete
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/procurement/vendors");
        setVendors(data.vendors || []);
      } catch { /* silent — dropdown just stays empty */ }
    })();
  }, [order.id]);

  // Clear any lingering selections when the parent order changes
  useEffect(() => { setSelected(new Set()); }, [order.id]);

  const total = items.length;
  const received = items.filter((i) => i.status === "delivered").length;
  const pct = total ? Math.round((received / total) * 100) : 0;
  // "Overdue" = expected date is in the past AND item hasn't been received yet.
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (it) => Boolean(
    it.expected_delivery_date &&
    it.expected_delivery_date < today &&
    !it.actual_delivery_date &&
    it.status !== "delivered"
  );
  const overdueCount = items.filter(isOverdue).length;

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const setStatus = async (itemId, status) => {
    try {
      await api.patch(`/procurement/orders/${order.id}/bom/${itemId}/status`, { status });
      toast.success(BOM_LABEL[status] || status);
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const patchDetails = async (itemId, patch) => {
    try {
      await api.patch(`/procurement/orders/${order.id}/bom/${itemId}/details`, patch);
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeItem = async (itemId) => {
    if (!window.confirm("Delete this BOM row?")) return;
    try {
      await api.delete(`/procurement/orders/${order.id}/bom/${itemId}`);
      toast.success("Removed");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (items.length === 0) {
    return (
      <div className="iz-card p-8 text-center" data-testid="bom-empty">
        <Package className="w-6 h-6 text-gray-700 mx-auto mb-2" />
        <p className="text-xs text-gray-500">No BOM items yet. Import a CSV to populate.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {/* Received / total summary + overdue count */}
      <div className="iz-card p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="bom-summary">
        <div className="flex items-center gap-3">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Received</p>
          <p className="font-display text-2xl text-white">
            {received} <span className="text-gray-500 text-lg">/ {total}</span>
          </p>
        </div>
        <div className="flex-1 max-w-[240px] h-1.5 bg-[#111] rounded overflow-hidden">
          <div className="h-full bg-emerald-400" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs font-mono text-gray-400">{pct}% fulfilled</p>
        {overdueCount > 0 && (
          <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-red-500/50 bg-red-500/10 text-red-300"
                data-testid="bom-overdue-count">
            {overdueCount} overdue
          </span>
        )}
      </div>

      {/* Bulk selection toolbar */}
      {canWrite && selected.size > 0 && (
        <div className="iz-card p-3 flex items-center justify-between gap-3 flex-wrap border-[#d4af37]/40"
             data-testid="bulk-toolbar">
          <p className="text-xs text-white">
            <span className="font-mono text-[#d4af37]">{selected.size}</span> row(s) selected
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setShowBulk(true)}
                    className="iz-btn-secondary text-xs"
                    data-testid="bulk-set-expected-btn">
              Set expected date…
            </button>
            <button onClick={() => setShowDispatch(true)}
                    className="iz-btn-primary text-xs flex items-center gap-1.5"
                    data-testid="bulk-mark-dispatched-btn">
              <Truck className="w-3.5 h-3.5"/> Mark as dispatched…
            </button>
            <button onClick={() => setSelected(new Set())}
                    className="iz-btn-secondary text-xs"
                    data-testid="bulk-clear-btn">
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="iz-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bom-table">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#1a1a1a] bg-[#080808]">
                {canWrite && (
                  <th className="px-3 py-2.5 w-8">
                    <input type="checkbox"
                           checked={selected.size === items.length}
                           onChange={toggleAll}
                           data-testid="bulk-select-all"/>
                  </th>
                )}
                <th className="text-left px-3 py-2.5">Item</th>
                <th className="text-left px-3 py-2.5">Vendor</th>
                <th className="text-right px-3 py-2.5">Qty · Unit</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th className="text-left px-3 py-2.5">Expected</th>
                <th className="text-left px-3 py-2.5">Actual</th>
                <th className="text-left px-3 py-2.5">Notes</th>
                {canWrite && <th className="px-3 py-2.5"></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <BomRow
                  key={it.id}
                  it={it}
                  canWrite={canWrite}
                  vendors={vendors}
                  overdue={isOverdue(it)}
                  isSelected={selected.has(it.id)}
                  onToggleSelect={() => toggleOne(it.id)}
                  onStatus={(s) => setStatus(it.id, s)}
                  onDetails={(patch) => patchDetails(it.id, patch)}
                  onRemove={() => removeItem(it.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <datalist id="bom-vendor-list">
        {vendors.map((v) => <option key={v} value={v} />)}
      </datalist>

      {showBulk && (
        <BulkExpectedDateModal
          orderId={order.id}
          itemIds={Array.from(selected)}
          onClose={() => setShowBulk(false)}
          onDone={() => { setShowBulk(false); setSelected(new Set()); onRefresh(); }}
        />
      )}
      {showDispatch && (
        <MarkDispatchedModal
          orderId={order.id}
          selectedItems={items.filter((i) => selected.has(i.id))}
          onClose={() => setShowDispatch(false)}
          onDone={() => { setShowDispatch(false); setSelected(new Set()); onRefresh(); }}
        />
      )}
    </div>
  );
}

function MarkDispatchedModal({ orderId, selectedItems, onClose, onDone }) {
  const [driver, setDriver] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/procurement/orders/${orderId}/bom/mark-dispatched`, {
        item_ids: selectedItems.map((i) => i.id),
        driver_name: driver, vehicle_number: vehicle, notes,
      });
      toast.success(`Dispatched ${selectedItems.length} item(s) — supervisor notified`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Dispatch failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         onClick={onClose} data-testid="dispatch-modal">
      <div className="iz-card w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Log Dispatch</p>
        <h2 className="font-display text-xl text-white mt-0.5 mb-3">
          Mark {selectedItems.length} item(s) as dispatched
        </h2>
        <p className="text-[11px] text-gray-500 mb-3 italic">
          BOM statuses stay untouched. The Site Supervisor will see a consolidated dispatch entry with a per-item breakdown on the Component Dispatch tab.
        </p>

        <div className="border border-[#1a1a1a] rounded p-3 mb-4 max-h-48 overflow-y-auto">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
            Items in this dispatch
          </p>
          <ul className="space-y-1">
            {selectedItems.map((it) => (
              <li key={it.id} className="text-[11px] text-gray-300 flex justify-between gap-3">
                <span className="truncate">{it.item_name}{it.vendor ? ` · ${it.vendor}` : ""}</span>
                <span className="font-mono text-gray-500 shrink-0">{it.qty}{it.unit ? ` ${it.unit}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Driver name (optional)</label>
            <input value={driver} onChange={(e) => setDriver(e.target.value)}
                   className="iz-input w-full text-xs"
                   data-testid="dispatch-driver"/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Vehicle no. (optional)</label>
            <input value={vehicle} onChange={(e) => setVehicle(e.target.value)}
                   className="iz-input w-full text-xs font-mono"
                   data-testid="dispatch-vehicle"/>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Notes (optional)</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    className="iz-input w-full text-xs"
                    data-testid="dispatch-notes"/>
        </div>

        <div className="mt-5 flex justify-end gap-2 pt-3 border-t border-[#1a1a1a]">
          <button onClick={onClose} className="iz-btn-secondary text-xs">Cancel</button>
          <button onClick={submit} disabled={busy}
                  className="iz-btn-primary text-xs"
                  data-testid="dispatch-submit">
            {busy ? "Dispatching…" : "Log dispatch"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkExpectedDateModal({ orderId, itemIds, onClose, onDone }) {
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!date) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/procurement/orders/${orderId}/bulk-bom-details`, {
        item_ids: itemIds,
        expected_delivery_date: date,
      });
      toast.success(`Updated ${data.modified} row(s)`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk update failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         onClick={onClose} data-testid="bulk-modal">
      <div className="iz-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Bulk Edit</p>
        <h2 className="font-display text-xl text-white mt-0.5 mb-4">
          Set expected date on {itemIds.length} row(s)
        </h2>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
          Expected delivery date
        </label>
        <input type="date"
               value={date}
               onChange={(e) => setDate(e.target.value)}
               className="iz-input w-full text-xs"
               data-testid="bulk-date-input"/>
        <div className="mt-5 flex justify-end gap-2 pt-3 border-t border-[#1a1a1a]">
          <button onClick={onClose} className="iz-btn-secondary text-xs">Cancel</button>
          <button onClick={submit} disabled={busy || !date}
                  className="iz-btn-primary text-xs"
                  data-testid="bulk-submit">
            {busy ? "Applying…" : "Apply to selected"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BomRow({ it, canWrite, vendors, overdue, isSelected, onToggleSelect, onStatus, onDetails, onRemove }) {
  // Local buffer so typing in a text/date cell doesn't fire a network call per
  // keystroke. Commit on blur if the value actually changed.
  const [buf, setBuf] = useState({
    vendor: it.vendor || "",
    expected_delivery_date: it.expected_delivery_date || "",
    actual_delivery_date: it.actual_delivery_date || "",
    notes: it.notes || "",
  });
  // Keep local state fresh whenever the server pushes updated fields
  useEffect(() => {
    setBuf({
      vendor: it.vendor || "",
      expected_delivery_date: it.expected_delivery_date || "",
      actual_delivery_date: it.actual_delivery_date || "",
      notes: it.notes || "",
    });
  }, [it.vendor, it.expected_delivery_date, it.actual_delivery_date, it.notes]);

  const commit = (key) => {
    const original = it[key] || "";
    if (buf[key] === original) return;
    onDetails({ [key]: buf[key] });
  };

  const cls = pillClass(bomTone(it.status));
  return (
    <tr className={`border-b border-[#0f0f0f] hover:bg-[#0a0a0a] align-top ${
          overdue ? "bg-red-500/5 border-l-2 border-l-red-500/60" : ""
        }`}
        data-testid={`bom-row-${it.id}`}>
      {canWrite && (
        <td className="px-3 py-2.5">
          <input type="checkbox"
                 checked={!!isSelected}
                 onChange={onToggleSelect}
                 data-testid={`bom-select-${it.id}`}/>
        </td>
      )}
      <td className="px-3 py-2.5 text-white min-w-[160px]">
        {it.item_name}
        {it.category && <span className="ml-2 text-[10px] font-mono text-gray-600">· {it.category}</span>}
        {it.sku && <p className="text-[10px] font-mono text-gray-600">SKU: {it.sku}</p>}
        {overdue && (
          <span className="mt-1 inline-block text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-red-500/50 bg-red-500/10 text-red-300"
                data-testid={`bom-overdue-${it.id}`}>
            Overdue
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 min-w-[140px]">
        {canWrite ? (
          <input
            list="bom-vendor-list"
            value={buf.vendor}
            onChange={(e) => setBuf((b) => ({ ...b, vendor: e.target.value }))}
            onBlur={() => commit("vendor")}
            placeholder="Vendor"
            className="iz-input text-xs w-full"
            data-testid={`bom-vendor-${it.id}`}
          />
        ) : (
          <span className="text-gray-300 text-xs">{it.vendor || "—"}</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-right text-white font-mono text-xs whitespace-nowrap">
        {it.qty}{it.unit ? ` ${it.unit}` : ""}
      </td>
      <td className="px-3 py-2.5 min-w-[130px]">
        {canWrite ? (
          <select
            value={it.status}
            onChange={(e) => onStatus(e.target.value)}
            className={`text-[10px] font-mono uppercase tracking-widest rounded px-1.5 py-0.5 border ${cls} outline-none`}
            data-testid={`bom-status-${it.id}`}
          >
            {BOM_EDIT_STATUSES.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        ) : (
          <span className={`text-[10px] font-mono uppercase tracking-widest rounded px-1.5 py-0.5 border ${cls}`}>
            {BOM_LABEL[it.status] || it.status}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 min-w-[130px]">
        {canWrite ? (
          <input
            type="date"
            value={buf.expected_delivery_date}
            onChange={(e) => setBuf((b) => ({ ...b, expected_delivery_date: e.target.value }))}
            onBlur={() => commit("expected_delivery_date")}
            className={`iz-input text-xs w-full ${overdue ? "border-red-500/60 text-red-300" : ""}`}
            data-testid={`bom-expected-${it.id}`}
          />
        ) : (
          <span className={`text-xs ${overdue ? "text-red-300 font-mono" : "text-gray-300"}`}>
            {it.expected_delivery_date || "—"}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 min-w-[130px]">
        {canWrite ? (
          <input
            type="date"
            value={buf.actual_delivery_date}
            onChange={(e) => setBuf((b) => ({ ...b, actual_delivery_date: e.target.value }))}
            onBlur={() => commit("actual_delivery_date")}
            className="iz-input text-xs w-full"
            data-testid={`bom-actual-${it.id}`}
          />
        ) : (
          <span className="text-gray-300 text-xs">{it.actual_delivery_date || "—"}</span>
        )}
      </td>
      <td className="px-3 py-2.5 min-w-[180px]">
        {canWrite ? (
          <input
            value={buf.notes}
            onChange={(e) => setBuf((b) => ({ ...b, notes: e.target.value }))}
            onBlur={() => commit("notes")}
            placeholder="Notes"
            className="iz-input text-xs w-full"
            data-testid={`bom-notes-${it.id}`}
          />
        ) : (
          <span className="text-gray-400 text-xs italic">{it.notes || "—"}</span>
        )}
      </td>
      {canWrite && (
        <td className="px-3 py-2.5">
          <button onClick={onRemove} className="p-1 text-gray-500 hover:text-red-400" data-testid={`remove-bom-${it.id}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </td>
      )}
    </tr>
  );
}

function ManifestsList({ order }) {
  const manifests = order.manifests || [];
  if (manifests.length === 0) {
    return <div className="iz-card p-8 text-center text-xs text-gray-500">No manifests logged yet.</div>;
  }
  return (
    <div className="space-y-2">
      {manifests.map(m => (
        <div key={m.id} className="iz-card p-4" data-testid={`manifest-${m.id}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Truck className="w-3.5 h-3.5 text-[#d4af37]" />
                <p className="font-mono text-sm text-white">{m.tracking_id}</p>
                {m.courier && <span className="text-[10px] font-mono text-gray-500">· {m.courier}</span>}
              </div>
              {m.remarks && <p className="text-xs text-gray-400 mt-1">{m.remarks}</p>}
            </div>
            <p className="text-[10px] font-mono text-gray-600">{new Date(m.created_at).toLocaleString()}</p>
          </div>
          {(m.file_ids || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {m.file_ids.map(fid => (
                <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                   className="iz-btn-secondary text-[10px] font-mono">
                  Attachment
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ImportBomModal({ order, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!file) { setErr("Choose a CSV file"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(`/procurement/orders/${order.id}/bom/import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${res.data.imported} row(s)`);
      onDone();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Import BOM" subtitle={order.project_name} testid="import-modal">
      <div className="space-y-4">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">CSV File</label>
          <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0])} className="text-xs text-white" data-testid="csv-input"/>
          <p className="text-[10px] font-mono text-gray-600 mt-2">
            Expected columns: item_name (required), category, vendor, sku, qty, unit, unit_cost, notes.
          </p>
          <a href={authedApiUrl(`/api/procurement/bom-template.csv`)}
             target="_blank" rel="noreferrer"
             className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#d4af37] hover:text-white transition-colors"
             data-testid="download-template">
            <FileDown className="w-3.5 h-3.5"/> Download sample template
          </a>
        </div>
        {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}
        <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
          <button onClick={onClose} className="iz-btn-secondary">Cancel</button>
          <button onClick={submit} disabled={busy || !file} className="iz-btn-primary" data-testid="csv-submit">
            {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ManifestModal({ order, onClose, onDone }) {
  const [tracking, setTracking] = useState("");
  const [courier, setCourier] = useState("");
  const [remarks, setRemarks] = useState("");
  const [bomId, setBomId] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!tracking.trim()) { setErr("Tracking ID required"); return; }
    setBusy(true);
    try {
      let file_ids = [];
      if (files.length > 0) {
        const uploaded = await uploadMany(files);
        file_ids = uploaded.map(u => u.id);
      }
      await api.post(`/procurement/orders/${order.id}/manifests`, {
        bom_item_id: bomId || null,
        tracking_id: tracking, courier, remarks, file_ids,
      });
      toast.success("Manifest logged");
      onDone();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Log Logistics Manifest" subtitle={order.project_name} testid="manifest-modal">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Tracking ID</label>
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} className="iz-input" required data-testid="tracking-input"/>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Courier</label>
            <input value={courier} onChange={(e) => setCourier(e.target.value)} className="iz-input" placeholder="e.g. Bluedart"/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Linked BOM Item (optional)</label>
            <select value={bomId} onChange={(e) => setBomId(e.target.value)} className="iz-input">
              <option value="">— No linked item —</option>
              {(order.bom_items || []).map(b => <option key={b.id} value={b.id}>{b.item_name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Remarks</label>
          <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="iz-input"/>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] font-mono uppercase tracking-widest text-gray-400">Cargo / container photos</label>
            <button type="button" onClick={() => inputRef.current?.click()} className="iz-btn-secondary text-xs flex items-center gap-1"><Camera className="w-3 h-3"/>Add</button>
            <input ref={inputRef} type="file" hidden multiple accept="image/*" onChange={(e) => setFiles([...files, ...Array.from(e.target.files)])}/>
          </div>
          <p className="text-[10px] font-mono text-gray-600">{files.length} photo(s) attached.</p>
        </div>
        {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}
        <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
          <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
          <button type="submit" disabled={busy} className="iz-btn-primary" data-testid="manifest-submit">
            {busy ? "Saving…" : "Log manifest"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ReportModal() { return null; }
function Kpi() { return null; }

function Modal({ children, onClose, title, subtitle, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid={testid}>
      <div className={`iz-card w-full ${wide ? "max-w-3xl" : "max-w-lg"} p-8 relative max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500">
          ✕
        </button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">{title}</p>
        {subtitle && <h2 className="font-display text-2xl text-white mb-6">{subtitle}</h2>}
        {children}
      </div>
    </div>
  );
}
